// Generated by Claude Code
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { Building2, RotateCcw } from 'lucide-react'
import { useCustomState } from '#core/state/State.ts'
import { useLimitedAccess } from '#core/state/LimitedAccessState.ts'
import { PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { CardModal } from '#components/primitives/components/card-modal.tsx'
import type { LimitedSlotRequest } from '#components/domain/auth/auth/shared/permissions-matrix.tsx'
import {
  LIMITED_ENTITY_SOURCES,
  LimitedAccessEditorState,
  type LimitedPropertyRow,
} from '#components/domain/auth/auth/shared/limited-access-editor-state.ts'

interface LimitedAccessEditorModalProps {
  /** The (service, slot) pair to configure, with its display label keys. */
  request: LimitedSlotRequest
  onClose: () => void
}

/** Humanizes a raw backend property key when no translation exists (e.g. `due_date` → "Due date"). */
function humanizeKey(key: string): string {
  const words = key
    .replace(/[_.]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase()
}

/**
 * Modal editor for one limited-access slot's COMPANY-WIDE property whitelist
 * (opened from the configure affordance next to a limited toggle in the
 * permissions matrix). Lists the entity's properties as a switch grid that
 * mirrors the matrix's advanced-permission layout; saving replaces the
 * whitelist for every role that uses the slot.
 *
 * Mount conditionally and key by `service`/`action` so the state reloads per slot.
 */
export function LimitedAccessEditorModal({ request, onClose }: LimitedAccessEditorModalProps) {
  const { t } = useTranslation('general')
  const source = LIMITED_ENTITY_SOURCES[request.service]
  // The entity namespace (work-orders / assets / parts) is loaded lazily and is
  // usually NOT yet in memory when this modal opens from the role editor. With
  // react-i18next's default Suspense mode, that missing namespace suspends the
  // component, which bubbles to the router's boundary and tears down + remounts
  // the whole role-management modal (resetting the matrix). `useSuspense: false`
  // renders immediately (labels fall back to the humanized key via `labelFor`,
  // then re-render once the namespace resolves) so the parent modal is never unmounted.
  const { t: tEntity } = useTranslation(source?.namespace ?? 'general', { useSuspense: false })
  const limitedAccess = useLimitedAccess()
  const state = useCustomState(() => new LimitedAccessEditorState(request.service, request.action))

  /**
   * Resolves a property row to its display label, matching the entity's table
   * columns: prefer the `@Field` label key, then the same `columns.<key>` the
   * table would use, and finally a humanized version of the raw key (never a
   * bare key) so nothing renders as a raw identifier.
   */
  const labelFor = (row: LimitedPropertyRow): string => {
    if (source && row.labelKey && i18n.exists(`${source.namespace}:${row.labelKey}`)) {
      return tEntity(row.labelKey)
    }
    if (source && i18n.exists(`${source.namespace}:columns.${row.key}`)) {
      return tEntity(`columns.${row.key}`)
    }
    return humanizeKey(row.key)
  }

  const allKeys = state.properties.map((row) => row.key)

  // Lay the properties out two-per-row in the bordered switch grid (same shape as
  // the matrix's advanced-permission sub-table).
  const propertyRows: LimitedPropertyRow[][] = []
  for (let i = 0; i < state.properties.length; i += 2) {
    propertyRows.push(state.properties.slice(i, i + 2))
  }

  const title = t('permissions.limitedAccess.title', {
    category: t(`permissions.categories.${request.categoryLabelKey}`),
    slot: t(`permissions.actions.${request.actionLabelKey}`),
  })

  const saveAndClose = () =>
    void state.save(() => {
      limitedAccess.reload()
      onClose()
    })

  // Reset keeps the modal open: the reloaded default whitelist is shown so the
  // user immediately sees what "default" means for this slot.
  const resetToDefault = () => void state.resetToDefault(() => limitedAccess.reload())

  const footer = (
    <div className="ynt-header-row-between">
      <div>
        {state.slot?.configured && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
            disabled={state.saving || state.loading}
            title={t('permissions.limitedAccess.resetToDefaultHint')}
            onClick={resetToDefault}
          >
            <RotateCcw size={14} />
            {t('permissions.limitedAccess.resetToDefault')}
          </button>
        )}
      </div>
      <div className="d-flex gap-2">
        <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
          {t('shared.actions.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={state.saving || state.loading || state.loadError}
          onClick={saveAndClose}
        >
          {state.saving && <span className="spinner-border spinner-border-sm me-1" />}
          {t('shared.actions.save')}
        </button>
      </div>
    </div>
  )

  return (
    <CardModal isOpen title={title} footer={footer} onClose={onClose} cardClassName="ynt-limited-editor">
      <div className="d-flex flex-column gap-3">
        <div className="alert alert-info ynt-info-alert-row" role="note">
          <Building2 size={16} className="flex-shrink-0 mt-1" />
          <span>{t('permissions.limitedAccess.companyWideNotice')}</span>
        </div>

        {request.service === PermissionService.WORK_ORDER_REQUESTS && (
          <div className="alert alert-info ynt-info-alert-row" role="note">
            <Building2 size={16} className="flex-shrink-0 mt-1" />
            <span>{t('permissions.actionsHelp.limitedViewOwnRequestsNote')}</span>
          </div>
        )}

        {state.loadError ? (
          <div className="alert alert-danger py-2 mb-0">{t('permissions.limitedAccess.loadError')}</div>
        ) : state.loading ? (
          <div className="d-flex justify-content-center py-4">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : (
          <>
            <div className="d-flex align-items-center gap-3">
              <button type="button" className="btn btn-link btn-sm p-0" onClick={() => state.selectAll(allKeys)}>
                {t('permissions.limitedAccess.selectAll')}
              </button>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-danger"
                onClick={() => state.clear(allKeys)}
              >
                {t('shared.common.clear')}
              </button>
              <span className="text-secondary ms-auto">
                {t('permissions.limitedAccess.selectedCount', {
                  selected: state.selected.length,
                  total: state.properties.length,
                })}
              </span>
            </div>

            <div className="table-responsive border">
              <table className="table table-sm table-bordered align-middle m-0 ynt-permadv-table">
                <tbody>
                  {propertyRows.map((pair, rowIdx) => (
                    <tr key={rowIdx}>
                      {pair.map((row) => {
                        const inputId = `limited-prop-${row.key}`
                        const checked = state.selected.includes(row.key)
                        return (
                          <td key={row.key} className="ynt-permadv-cell px-3">
                            <div className="d-flex align-items-center gap-2">
                              <label htmlFor={inputId} className="form-check-label flex-grow-1 m-0 cursor-pointer">
                                {labelFor(row)}
                              </label>
                              <span className="form-switch ynt-permswitch-wrap d-inline-flex m-0 flex-shrink-0">
                                <input
                                  type="checkbox"
                                  role="switch"
                                  className="form-check-input ynt-permswitch"
                                  id={inputId}
                                  checked={checked}
                                  onChange={() => state.toggle(row.key)}
                                />
                              </span>
                            </div>
                          </td>
                        )
                      })}
                      {pair.length === 1 && <td className="ynt-permadv-cell" aria-hidden="true" />}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {state.saveError && (
              <div className="alert alert-danger py-2 mb-0">{t('permissions.limitedAccess.saveError')}</div>
            )}
          </>
        )}
      </div>
    </CardModal>
  )
}
